import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";


import ProfileReportService from "../../services/profile/ProfileReportService";
import { resetProfileReportState, setDropdownReports, setReportsByProfile, toggleReportStatus } from "../../store/slices/profile/profileReportSlice";

/**
 * 📊 Hook para administrar los reportes por perfil
 *
 * Encapsula toda la lógica de:
 * - Carga de reportes de un perfil
 * - Carga de reportes globales
 * - Activación/desactivación de reportes
 * - Manejo de errores, alertas y loading
 */
const useProfileReport = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const { reports, dropdownReports } = useSelector(
    (state: RootState) => state.profileReport
  );

  // =====================================================
  // 🌐 CONSULTAS BASE
  // =====================================================

  /** 🔹 Cargar los reportes asociados a un perfil */
  const fetchReportsByProfile = async (profileId: number) => {
    try {
      showLoadingIndicator();
      const response = await ProfileReportService.getReportsByProfile(profileId);
      const { statusCode, type, title, message, data } = response;
    //   console.log(data);

      if (statusCode === 200 && Array.isArray(data)) {
        dispatch(setReportsByProfile(data));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  /** 🔹 Cargar todos los reportes globales para dropdowns */
  const fetchAllReportsForDropdown = async () => {
    try {
      showLoadingIndicator();
      const response = await ProfileReportService.getAllReportsForDropdown();
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200 && Array.isArray(data)) {
        dispatch(setDropdownReports(data));
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =====================================================
  // 🎯 ACTUALIZACIONES (TOGGLE)
  // =====================================================

  /** 🔹 Activar o desactivar un reporte */
  const toggleReport = async (payload: {
    profileId: number;
    reportId: number;
    enable: boolean;
  }) => {
    // console.log(payload);
    try {
      showLoadingIndicator();
      const response = await ProfileReportService.toggleReportForProfile(payload);
      console.log(response);
      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {
        // ✅ data es boolean (true = enabled, false = disabled)
        dispatch(
          toggleReportStatus({
            reportId: payload.reportId,
            enabled: data === true,
          })
        );
      }

      showAlert(type, title, message);
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  // =====================================================
  // 🧩 UTILIDADES
  // =====================================================
  const reset = () => dispatch(resetProfileReportState());

  // =====================================================
  // 📦 RETORNO DEL HOOK
  // =====================================================
  return {
    reports,
    dropdownReports,

    // Fetchers
    fetchReportsByProfile,
    fetchAllReportsForDropdown,

    // Actions
    toggleReport,
    reset,
  };
};

export default useProfileReport;
